import { IAppointment } from "@base/modules/appointment/appointment.interface";
import { ISurveyAnswer, ISurveyPayload, ISurveyQuestion, ISurveyReviewItem } from "./checkoutSurvey.interface";

const SURVEY_KEYS = ["checkout_survey", "checkout_survery"];

const parseExtra = (raw: unknown): Record<string, unknown> => {
  if (!raw) {
    return {};
  }

  if (typeof raw === "object") {
    return raw as Record<string, unknown>;
  }

  try {
    return JSON.parse(String(raw)) || {};
  } catch {
    return {};
  }
};

const codesIn = (extra: Record<string, unknown>) => {
  for (const key of SURVEY_KEYS) {
    const value = extra[key];

    if (value === null || value === undefined) {
      continue;
    }

    const codes = (Array.isArray(value) ? value : String(value).split(",")).map((code) => String(code).trim()).filter(Boolean);

    if (codes.length) {
      return codes;
    }
  }

  return [];
};

/**
 * The survey an appointment carries, if any. The code may sit on the appointment or - as the
 * backend does today - on one of its used services; the misspelt key is read too, just in case.
 * The code stays there after the survey is done, so it never says whether it was done.
 */
export const getSurveyCode = (appointment: IAppointment & { extra_info?: unknown }): string | undefined => {
  let codes = codesIn(parseExtra(appointment.extra_info));

  for (const service of appointment.appointment_used_services ?? []) {
    if (codes.length) {
      break;
    }

    codes = codesIn(parseExtra(service.extra_info));
  }

  return codes[0];
};

export type IAnswerInput = "choice" | "choiceWithText" | "text" | "number";

const NUMBER_FORMAT = /^number\(\s*(\d+)\s*,\s*(\d+)\s*\)/i;

/** How one answer is filled in - decided per answer, so one question can mix kinds. */
export const getAnswerInput = (answer: ISurveyAnswer): IAnswerInput => {
  if (NUMBER_FORMAT.test((answer.answer_description ?? "").trim())) {
    return "number";
  }

  switch (answer.answer_type) {
    case "SELECTION":
      return "choice";
    case "SELECTION_TEXT":
      return "choiceWithText";
    case "TEXT":
      return "text";
    default:
      return "number";
  }
};

/** Whether a number box takes decimals: `number(a,b)` with b > 0. */
export const allowsDecimals = (answer: ISurveyAnswer) => Number(NUMBER_FORMAT.exec((answer.answer_description ?? "").trim())?.[2] ?? 0) > 0;

/** Answers whose chosen value carries what the patient typed. */
export const isFreeText = (answer: ISurveyAnswer) =>
  (answer.answer_type ?? "").toUpperCase().includes("TEXT") || (answer.answer_description ?? "").trim().toLowerCase().startsWith("number(");

export const isMultiple = (question: ISurveyQuestion) => question.question_type === "MULTIPLE_ANSWER";

/** The single-question endpoint can leave fields out; the group's summary fills them in. */
export const mergeQuestion = (detail: ISurveyQuestion, summary?: ISurveyQuestion): ISurveyQuestion => ({
  ...detail,
  question_type: detail.question_type || summary?.question_type,
  question_name: detail.question_name || summary?.question_name,
  position: detail.position ?? summary?.position,
  // The group's flag wins whenever it has one, as on iOS.
  is_mandatory: summary?.is_mandatory ?? detail.is_mandatory,
});

export const sortByPosition = (questions: ISurveyQuestion[] = []) => [...questions].sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));

/** One page of the survey as the patient left it. */
export interface ISurveyPage {
  question: ISurveyQuestion;
  selected: Record<string, boolean>;
  typed: Record<string, string>;
}

const codeOf = (answer: ISurveyAnswer) => (answer.answer_code ?? "").trim();

const isChosen = (page: ISurveyPage, answer: ISurveyAnswer) => !!page.selected[codeOf(answer)];

/** Whether the patient may move past this page. */
export const canProceed = (page: ISurveyPage) => {
  const answers = page.question.answer ?? [];

  // A question set up without answers must not trap the patient.
  if (!answers.length) {
    return true;
  }

  if (answers.some((answer) => answer.answer_type === "SELECTION_TEXT")) {
    return answers.filter((answer) => answer.is_mandatory).every((answer) => isChosen(page, answer));
  }

  if (page.question.is_mandatory === false) {
    return true;
  }

  return answers.some((answer) => isChosen(page, answer));
};

/**
 * The next question: where the first chosen answer branches to, else the one after this in the
 * group's order. `undefined` means this page is the last one.
 */
export const getNextQuestionCode = (page: ISurveyPage, orderedCodes: string[]) => {
  const branch = (page.question.answer ?? []).find((answer) => isChosen(page, answer))?.question_relate_code?.trim();

  if (branch) {
    return branch;
  }

  const index = orderedCodes.indexOf(String(page.question.question_code));

  return index >= 0 && index < orderedCodes.length - 1 ? orderedCodes[index + 1] : undefined;
};

/**
 * The `survey` value. Only pages actually visited get a key; a visited page left blank keeps an
 * empty list so the backend can tell "skipped" from "never shown".
 */
export const buildSurveyPayload = (group: string, lang: string, pages: ISurveyPage[]): ISurveyPayload => {
  const answers: Record<string, string[]> = {};

  pages.forEach((page) => {
    const questionCode = (page.question.question_code ?? "").trim();

    if (!questionCode) {
      return;
    }

    answers[questionCode] = (page.question.answer ?? [])
      .filter((answer) => isChosen(page, answer) && codeOf(answer))
      .map((answer) => {
        const typed = (page.typed[codeOf(answer)] ?? "").trim();

        return isFreeText(answer) && typed ? `${codeOf(answer)}:${typed}` : codeOf(answer);
      });
  });

  return { group, lang, answers };
};

/** Splits a stored entry at its first ":" - answer codes never contain one, typed text may. */
export const parseSurveyEntry = (entry: string) => {
  const index = entry.indexOf(":");

  return index < 0 ? { code: entry, typed: undefined } : { code: entry.slice(0, index), typed: entry.slice(index + 1) };
};

export const parseSurveyPayload = (raw?: string): ISurveyPayload | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" && parsed.answers ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Resolves the stored codes back to question and answer names, in the group's order. */
export const buildReviewItems = (payload: ISurveyPayload, questions: ISurveyQuestion[]): ISurveyReviewItem[] =>
  sortByPosition(questions)
    .filter((question) => question.question_code && payload.answers[question.question_code])
    .map((question) => ({
      questionCode: String(question.question_code),
      questionName: question.question_name ?? question.question_value ?? String(question.question_code),
      isMultiple: isMultiple(question),
      entries: payload.answers[String(question.question_code)].map((entry) => {
        const { code, typed } = parseSurveyEntry(entry);
        const answer = question.answer?.find((item) => codeOf(item) === code);

        return { name: answer?.answer_name ?? "", typed, isFreeText: answer ? isFreeText(answer) : !!typed };
      }),
    }));

export type IRatingView =
  | "hidden"
  /** The survey is still to be done: it replaces the rating block. */
  | "survey"
  /** Stars (and, without a survey, a comment) can be given. */
  | "open"
  /** Not rated and past the 48 hour window: shown locked. */
  | "expired"
  /** Already rated: read-only. */
  | "rated";

export interface IRatingViewInput {
  appointment: IAppointment;
  surveyCode?: string;
  rating?: { id?: number | string; survey?: string };
  /** The survey was answered on this browser (waiting to be sent, or kept for review). */
  isSurveyDoneHere: boolean;
  now?: number;
}

const REVIEW_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * What the rating area shows. A checkout survey takes priority (user decision 2026-10-13): a completed
 * appointment carrying one invites it whenever it is not rated yet, and its stars stay open past the
 * 48 hour window so the answers can be sent with them. Without a survey the window still applies.
 */
export const getRatingView = ({ appointment, surveyCode, rating, isSurveyDoneHere, now = Date.now() }: IRatingViewInput): IRatingView => {
  // `payment_status: null` counts as paid; an unpaid appointment shows its pay button instead.
  if (appointment.status !== "COMPLETED" || appointment.payment_status === false) {
    return "hidden";
  }

  const isRated = Number(rating?.id ?? 0) > 0;
  const endTime = Number(appointment.time_table_period?.period_end_time);
  // Without an end time the window cannot be checked, so it counts as closed.
  const isWithinWindow = !!endTime && now - endTime <= REVIEW_WINDOW_MS;
  const needsSurvey = !!surveyCode && !rating?.survey && !isSurveyDoneHere;

  if (needsSurvey && !isRated) {
    return "survey";
  }

  if (isRated) {
    return "rated";
  }

  return isWithinWindow || !!surveyCode ? "open" : "expired";
};

const PENDING_PREFIX = "checkout_survey_pending:";
const REVIEW_PREFIX = "checkout_survey_review:";

/** Storage can be blocked (private mode); every access is guarded and simply does nothing then. */
const storage = {
  get(key: string) {
    try {
      return localStorage.getItem(key) ?? undefined;
    } catch {
      return undefined;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
};

export const surveyStorageId = (appointment: IAppointment) => String(appointment.key || appointment.id || "");

export const surveyStorage = {
  getPending: (id: string) => storage.get(PENDING_PREFIX + id),
  setPending: (id: string, survey: string) => storage.set(PENDING_PREFIX + id, survey),
  clearPending: (id: string) => storage.remove(PENDING_PREFIX + id),
  getReview: (id: string): ISurveyReviewItem[] | undefined => {
    const raw = storage.get(REVIEW_PREFIX + id);

    try {
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  },
  setReview: (id: string, items: ISurveyReviewItem[]) => storage.set(REVIEW_PREFIX + id, JSON.stringify(items)),
};
